import { StyledOrderInvoiceBase } from './style';
import { Divider } from '../../../../../ui';
import { Field } from './Field';
import { useIntl } from 'react-intl';
import { Transaction } from '../../../../../interfaces/BackendModels';
import { DeliveryType } from '../../../../../types/DeliveryType';
import { useDeliveryRedirects } from '../../../../../utils/useDeliveryRedirects';
import calculateVatFromTotalPrice from 'utils/pvnHelper';

interface OrderInvoiceBaseProps {
  transaction?: Transaction | null;
  order?: any;
  selectedTab?: string;
}

export const OrderInvoiceBase = ({ transaction, order, selectedTab }: OrderInvoiceBaseProps) => {
  const intl = useIntl();

  let farmProductTotalWithVAT = 0.0;
  let totalVAT = 0.0;
  transaction?.items.forEach((item) => {
    farmProductTotalWithVAT += Number(item.stock_total);
    totalVAT += calculateVatFromTotalPrice(Number(item.stock_total), item.vat);
  });

  const deliveryRedirects = useDeliveryRedirects();
  const isWholesale = ['purchase', 'reservation', 'dynamic_purchase', 'order'].includes(
    transaction?.transaction_type?.toLowerCase() ?? ''
  );

  const showPvn = !isWholesale || !!transaction?.farm?.pvn_nr;

  const deliveryPrice = isWholesale
    ? (transaction?.fulfillments ?? []).reduce(
        (sum, f) => sum + Number(f.delivery_price ?? 0),
        0
      ) || (transaction?.delivery_conditions ?? []).reduce(
        (sum, dc) => sum + Number(dc.delivery_price ?? 0),
        0
      )
    : Number(transaction?.delivery?.price ?? 0);

  const totalSum = (Number(farmProductTotalWithVAT) + deliveryPrice).toFixed(2);

  const handleArrangeDelivery = () => {
    const type = transaction?.delivery?.type as DeliveryType | undefined;
    const orderId = transaction?.order_id;

    if (!type || orderId == null) return;

    if (deliveryRedirects[type]) {
      deliveryRedirects[type](orderId);
    }
  };

  const showSellerInfo = selectedTab === 'my_orders' && isWholesale;

  const firstColumn = [
    {
      labelKey: showSellerInfo ? 'invoice.supplier' : 'invoice.client',
      value: showSellerInfo
        ? (transaction?.farm?.name ?? '')
        : (transaction?.customer_name ?? order?.customer_name ?? ''),
    },
    {
      labelKey: 'invoice.phone',
      value: showSellerInfo
        ? (transaction?.farm?.contact_phone ?? '')
        : (transaction?.buyer_farm?.contact_phone ?? transaction?.customer_phone ?? transaction?.phone ?? order?.customer_phone ?? order?.phone),
    },
    {
      labelKey: 'invoice.email',
      value: showSellerInfo
        ? (transaction?.farm?.contact_email ?? '')
        : (transaction?.buyer_farm?.contact_email ?? transaction?.customer_email ?? order?.customer_email ?? ''),
    },
    ...(!isWholesale
      ? [
          {
            labelKey: 'invoice.delivery_type',
            value: intl.formatMessage({
              id: `delivery.${transaction?.delivery?.type}`,
            }),
          },
          {
            labelKey: 'invoice.delivery_address',
            value: transaction?.items[0]?.delivery_address ?? '',
          },
        ]
      : []),
  ];

  const secondColumn = [
    {
      labelKey: showPvn ? 'invoice.price' : 'invoice.price_no_pvn',
      value: showPvn
        ? `€ ${(farmProductTotalWithVAT - totalVAT).toFixed(2)}`
        : `€ ${farmProductTotalWithVAT.toFixed(2)}`,
    },
    ...(showPvn
      ? [
          { labelKey: 'invoice.pvn', value: `€ ${totalVAT.toFixed(2)}` },
          {
            labelKey: 'invoice.price_with_pvn',
            value: `€ ${farmProductTotalWithVAT.toFixed(2)}`,
          },
        ]
      : []),
    {
      labelKey: showPvn ? 'invoice.delivery' : 'invoice.delivery_no_pvn',
      value: `€ ${deliveryPrice.toFixed(2)}`,
    },
    {
      labelKey: showPvn ? 'invoice.total' : 'invoice.total_no_pvn',
      value: `€ ${totalSum ?? ''}`,
    },
  ];

  return (
    <StyledOrderInvoiceBase className="invoice-content">
      <div className="content">
        <div className="invoice-data">
          <div className="first">
            {firstColumn.map((fieldDef) => (
              <Field
                key={fieldDef.labelKey}
                labelKey={fieldDef.labelKey}
                value={fieldDef.value}
              />
            ))}
          </div>
          <div className="second">
            {secondColumn.map((fieldDef) => (
              <Field
                key={fieldDef.labelKey}
                labelKey={fieldDef.labelKey}
                value={fieldDef.value}
              />
            ))}
          </div>
        </div>

        <div className={'divider'}>
          <Divider maxWidth={'100%'} />
        </div>

        {!isWholesale && (
          <div className="footer">
            <div className={'arrange-delivery'} onClick={handleArrangeDelivery}>
              {intl.formatMessage({ id: 'invoice.checkout' })}
            </div>
          </div>
        )}
      </div>
    </StyledOrderInvoiceBase>
  );
};
